<template>
  <div class="vod-question-page">
    <!-- ===== 点位列表 ===== -->
    <div class="toolbar">
      <a-select v-model="pFilter.sourceType" placeholder="来源类型" allow-clear style="width: 170px">
        <a-select-option value="VOD_SINGLE">VOD_SINGLE 单视频</a-select-option>
        <a-select-option value="MIX_SET_TOPIC">MIX_SET_TOPIC 专题子条目</a-select-option>
      </a-select>
      <a-input v-model="pFilter.videoTitle" placeholder="视频标题搜索" allow-clear style="width: 200px; margin-left: 8px" @keyup.enter="loadPoints(1)" />
      <a-button type="primary" style="margin-left: 8px" @click="loadPoints(1)">搜索</a-button>
      <a-button type="primary" icon="plus" style="float: right" @click="openPointModal()">新增点位</a-button>
    </div>
    <a-table
      :columns="pointColumns"
      :data-source="points"
      row-key="id"
      size="middle"
      :loading="pLoading"
      :scroll="{ x: 1000 }"
      :pagination="{ current: pPage, pageSize: pPageSize, total: pTotal, showSizeChanger: false }"
      @change="onPTableChange"
    >
      <template slot="qTitle" slot-scope="text, record">
        {{ qTitleOf(record) }}
        <span class="mono">#{{ record.questionnaireId }}</span>
      </template>
      <template slot="sourceType" slot-scope="text">
        <a-tag :color="text === 'VOD_SINGLE' ? 'blue' : 'purple'">{{ text }}</a-tag>
      </template>
      <template slot="source" slot-scope="text, record">
        <b>{{ record.sourceTitle || '—' }}</b>
        <span class="mono">#{{ record.sourceId }}</span>
      </template>
      <template slot="topic" slot-scope="text, record">
        <span v-if="record.topicTitle">{{ record.topicTitle }}</span>
        <span v-else class="cfg-hint">—</span>
      </template>
      <template slot="status" slot-scope="text">
        <a-tag :color="text === 1 ? 'green' : 'red'">{{ text === 1 ? '启用' : '禁用' }}</a-tag>
      </template>
      <template slot="auth" slot-scope="text">
        <a-tag v-if="text === 1" color="blue">需认证</a-tag>
        <span v-else class="cfg-hint">—</span>
      </template>
      <template slot="triggerTime" slot-scope="text">
        <b>{{ text }}</b>s
      </template>
      <template slot="action" slot-scope="text, record">
        <a-button size="small" @click="showStat(record.id)">统计</a-button>
        <a-button size="small" style="margin-left: 6px" @click="exportExcel(record.id)">导出</a-button>
        <a-button size="small" style="margin-left: 6px" @click="togglePointStatus(record.id, record.status === 1 ? 0 : 1)">
          {{ record.status === 1 ? '禁用' : '启用' }}
        </a-button>
        <a-button size="small" style="margin-left: 6px" @click="openPointModal(record.id)">编辑</a-button>
        <a-popconfirm title="确认删除该点位？作答记录保留" @confirm="delPoint(record.id)">
          <a-button size="small" type="danger" style="margin-left: 6px">删除</a-button>
        </a-popconfirm>
      </template>
    </a-table>

    <!-- ===== 点位 新增/编辑 弹窗 ===== -->
    <a-modal :visible="pModal.visible" :title="pModal.id ? '编辑点位 #' + pModal.id : '新增点位'" width="680px" @cancel="closePModal" @ok="savePoint" :confirm-loading="pModal.saving">
      <a-form-model :label-col="{ span: 5 }" :wrapper-col="{ span: 18 }">
        <a-form-model-item label="来源类型">
          <a-radio-group v-model="pForm.sourceType" @change="onPTypeChange">
            <a-radio value="VOD_SINGLE">VOD_SINGLE 单视频</a-radio>
            <a-radio value="MIX_SET_TOPIC">MIX_SET_TOPIC 专题子条目</a-radio>
          </a-radio-group>
        </a-form-model-item>

        <!-- 专题选择：仅 MIX_SET_TOPIC -->
        <a-form-model-item v-if="pForm.sourceType === 'MIX_SET_TOPIC'" label="选择专题" required>
          <a-select
            v-model="selectedTopicId"
            show-search
            placeholder="输入专题名称搜索"
            :filter-option="false"
            :not-found-content="topicLoading ? '加载中…' : '未找到匹配专题'"
            style="width: 300px"
            @search="debouncedTopicSearch"
            @change="onTopicPicked"
          >
            <a-select-option v-for="t in topicOptions" :key="t.id" :value="t.id">{{ t.title }} #{{ t.id }}</a-select-option>
          </a-select>
        </a-form-model-item>

        <a-form-model-item :label="pForm.sourceType === 'MIX_SET_TOPIC' ? '选择子视频' : '选择视频'" required>
          <a-select
            v-model="selectedVideoId"
            show-search
            placeholder="输入视频标题搜索"
            :filter-option="false"
            :not-found-content="videoLoading ? '加载中…' : (pForm.sourceType === 'MIX_SET_TOPIC' && !selectedTopicId ? '请先在上方选择专题' : '未找到匹配视频')"
            style="width: 360px"
            @search="debouncedVideoSearch"
            @change="onVideoPicked"
          >
            <a-select-option v-for="v in videoOptions" :key="v.id" :value="v.id">{{ v.title }} <template v-if="v.topicTitle">（{{ v.topicTitle }}）</template> #{{ v.id }}</a-select-option>
          </a-select>
          <div v-if="selectedVideo" class="cfg-hint" style="margin-top: 4px">
            已选：#{{ selectedVideo.id }} {{ selectedVideo.title }}
            <template v-if="selectedVideo.topicTitle">，所属专题：{{ selectedVideo.topicTitle }}</template>
            <template v-else>（独立视频，未挂专题）</template>
          </div>
        </a-form-model-item>

        <a-form-model-item label="关联问卷" required>
          <a-select v-model="pForm.questionnaireId" show-search option-filter-prop="label" placeholder="选择问卷" style="width: 300px">
            <a-select-option v-for="q in questionnaireOptions" :key="q.id" :value="q.id" :label="q.id + ' · ' + q.title">{{ q.id }} · {{ q.title }}</a-select-option>
          </a-select>
        </a-form-model-item>

        <a-form-model-item label="触发秒数" required>
          <a-input-number v-model="pForm.triggerTime" :min="0" style="width: 200px" />
          <span class="cfg-hint" style="margin-left: 8px">播放到该秒弹窗；跳过不算已操作，下次进入仍会触发</span>
        </a-form-model-item>

        <a-form-model-item label="认证提示">
          <a-switch v-model="pForm.requireAuth" checked-children="需认证" un-checked-children="否" />
          <span class="cfg-hint" style="margin-left: 8px">开启 = 弹出问卷弹窗后，弹出认证弹窗给用户确认认证信息</span>
        </a-form-model-item>
      </a-form-model>
    </a-modal>

    <!-- ===== 统计弹窗 ===== -->
    <a-modal :visible="statModal.visible" :title="'点位统计 · 点位 #' + statModal.pointId" :footer="null" width="780px" @cancel="closeStatModal">
      <div v-if="statLoading" class="stat-loading">加载中…</div>
      <div v-else-if="!statData" class="stat-loading">暂无统计数据</div>
      <div v-else>
        <div class="cfg-hint" style="margin-bottom: 8px">
          弹出次数 = 弹窗出现时播放端上报的 POP 记录数（用户关闭浏览器/强制刷新也计入）；作答率 = 完成 / 弹出次数
        </div>
        <div class="stat-grid">
          <div class="stat-card"><div class="k">弹出次数</div><div class="v">{{ statData.popCount }}</div></div>
          <div class="stat-card"><div class="k">完成作答</div><div class="v">{{ statData.finishCount }}</div></div>
          <div class="stat-card"><div class="k">跳过</div><div class="v">{{ statData.skipCount }}</div></div>
          <div v-if="scored" class="stat-card"><div class="k">平均分</div><div class="v">{{ statData.avgScore }}</div></div>
          <div class="stat-card"><div class="k">作答率</div><div class="v">{{ statData.answerRate }}</div></div>
          <div class="stat-card"><div class="k">跳过率</div><div class="v">{{ statData.skipRate }}</div></div>
          <div class="stat-card"><div class="k">完成率</div><div class="v">{{ statData.finishRate }}</div></div>
        </div>

        <div v-if="scored" class="stat-block">
          <div class="stat-block-title">得分分布（人数）</div>
          <div v-for="k in scoreDistKeys" :key="k" class="bar-row">
            <span class="bar-lbl">{{ k.replace('_', '~') }}</span>
            <div class="bar-track"><div class="bar-fill" :style="{ width: barWidth(statData.scoreDistribute[k], scoreDistMax) }"></div></div>
            <span class="bar-num">{{ statData.scoreDistribute[k] || 0 }}</span>
          </div>
        </div>

        <div v-for="q in statData.questionStats" :key="q.questionId" class="stat-block">
          <div class="stat-block-title">
            {{ q.questionTitle }}
            <a-tag :color="typeColor(q.type)" style="margin-left: 6px">{{ typeLabel(q.type) }}</a-tag>
          </div>
          <div class="cfg-hint" style="margin: 4px 0">
            作答 {{ q.answerCount }} 人
            <template v-if="q.rightRate != null"> · 答对 {{ q.rightCount }} 人 · 正确率 {{ q.rightRate }}</template>
            <template v-else> · 未计分</template>
          </div>
          <div v-for="(v, k) in q.optionDistribute" :key="k" class="bar-row">
            <span class="bar-lbl wide" :title="k">{{ k }}</span>
            <div class="bar-track"><div class="bar-fill" :style="{ width: barWidth(v, optionDistMax(q)) }"></div></div>
            <span class="bar-num">{{ v }}</span>
          </div>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { get, post, put, del, EXPORT_BASE } from './api'
import { typeLabel, typeColor } from './util'

export default {
  name: 'VodPoint',
  mounted() {
    this.loadPoints(1)
  },
  data() {
    return {
      points: [],
      pLoading: false,
      pPage: 1,
      pPageSize: 10,
      pTotal: 0,
      pFilter: { sourceType: undefined, videoTitle: '' },

      pModal: { visible: false, id: null, saving: false },
      pForm: { sourceType: 'VOD_SINGLE', questionnaireId: undefined, triggerTime: undefined },
      questionnaireOptions: [],
      topicOptions: [],
      videoOptions: [],
      selectedTopicId: undefined,
      selectedVideoId: undefined,
      selectedTopic: null,
      selectedVideo: null,
      topicLoading: false,
      videoLoading: false,
      topicSearchTimer: null,
      videoSearchTimer: null,

      statModal: { visible: false, pointId: null },
      statData: null,
      statLoading: false
    }
  },
  computed: {
    pointColumns() {
      return [
        { title: '问卷名称', key: 'qTitle', width: 200, scopedSlots: { customRender: 'qTitle' } },
        { title: 'ID', dataIndex: 'id', width: 70 },
        { title: '来源类型', dataIndex: 'sourceType', width: 140, scopedSlots: { customRender: 'sourceType' } },
        { title: '所属专题', key: 'topic', width: 160, scopedSlots: { customRender: 'topic' } },
        { title: '视频/子视频', key: 'source', width: 220, scopedSlots: { customRender: 'source' } },
        { title: '状态', dataIndex: 'status', width: 80, scopedSlots: { customRender: 'status' } },
        { title: '认证', dataIndex: 'requireAuth', width: 80, scopedSlots: { customRender: 'auth' } },
        { title: '触发时间', dataIndex: 'triggerTime', width: 100, scopedSlots: { customRender: 'triggerTime' } },
        { title: '创建时间', dataIndex: 'createdTime', width: 170 },
        { title: '操作', key: 'action', width: 320, scopedSlots: { customRender: 'action' } }
      ]
    },
    scored() {
      return this.statData && this.statData.avgScore != null
    },
    scoreDistKeys() {
      return ['0_30', '30_60', '60_80', '80_100']
    },
    scoreDistMax() {
      const dist = (this.statData && this.statData.scoreDistribute) || {}
      return Math.max(1, ...Object.values(dist).map(Number))
    }
  },
  methods: {
    typeLabel,
    typeColor,

    qTitleOf(record) {
      return record.questionnaireTitle || '—'
    },

    barWidth(v, max) {
      return Math.round(((v || 0) / max) * 100) + '%'
    },
    optionDistMax(q) {
      const opts = q.optionDistribute || {}
      return Math.max(1, ...Object.values(opts).map(Number))
    },

    /* ===== 点位列表 ===== */
    loadPoints(page) {
      this.pLoading = true
      get('/vod/questionnaire/point/list', {
        sourceType: this.pFilter.sourceType || undefined,
        videoTitle: this.pFilter.videoTitle || undefined,
        pageNum: page || 1,
        pageSize: this.pPageSize
      }).then(body => {
        this.points = body.data || []
        this.pTotal = body.count != null ? body.count : this.points.length
        this.pPage = page || 1
      }).catch(e => {
        this.$message.error(e.message)
      }).finally(() => {
        this.pLoading = false
      })
    },
    onPTableChange(pagination) {
      this.loadPoints(pagination.current)
    },
    togglePointStatus(id, status) {
      put('/vod/questionnaire/point', { id: id, status: status }).then(() => {
        this.$message.success(status === 1 ? '点位已启用' : '点位已禁用')
        this.loadPoints(this.pPage)
      }).catch(e => this.$message.error(e.message))
    },
    delPoint(id) {
      del('/vod/questionnaire/point/' + id).then(() => {
        this.$message.success('已删除')
        this.loadPoints(this.pPage)
      }).catch(e => this.$message.error(e.message))
    },
    exportExcel(pointId) {
      window.open(EXPORT_BASE + '/vod/questionnaire/point/' + pointId + '/export', '_blank')
    },

    /* ===== 点位弹窗 ===== */
    loadQuestionnaireOptions() {
      get('/vod/questionnaire/list', { pageNum: 1, pageSize: 200 }).then(body => {
        this.questionnaireOptions = body.data || []
      }).catch(e => this.$message.error('问卷下拉加载失败：' + e.message))
    },
    openPointModal(id) {
      this.pModal.id = id || null
      this.pForm = { sourceType: 'VOD_SINGLE', questionnaireId: undefined, triggerTime: undefined, requireAuth: false }
      this.selectedTopic = null
      this.selectedTopicId = undefined
      this.selectedVideo = null
      this.selectedVideoId = undefined
      this.topicOptions = []
      this.videoOptions = []
      this.pModal.visible = true
      this.loadQuestionnaireOptions()
      if (id) {
        get('/vod/questionnaire/point/list', { pageNum: 1, pageSize: 200 }).then(body => {
          const all = body.data || []
          const p = all.filter(x => x.id === id)[0]
          if (!p) return
          this.pForm = {
            sourceType: p.sourceType || 'VOD_SINGLE',
            questionnaireId: p.questionnaireId,
            triggerTime: p.triggerTime != null ? p.triggerTime : undefined,
            requireAuth: p.requireAuth === 1
          }
          get('/vod/questionnaire/video/search', { type: p.sourceType, id: p.sourceId, limit: 1 }).then(vb => {
            const vlist = vb.data || []
            if (!vlist.length) {
              this.selectedVideo = { id: p.sourceId, title: 'ID ' + p.sourceId, topicId: null, topicTitle: null }
              this.selectedVideoId = p.sourceId
              this.videoOptions = [{ id: p.sourceId, title: 'ID ' + p.sourceId }]
              return
            }
            const v = vlist[0]
            this.selectedVideo = { id: v.id, title: v.title, topicId: v.topicId, topicTitle: v.topicTitle }
            this.selectedVideoId = v.id
            this.videoOptions = [{ id: v.id, title: v.title, topicId: v.topicId, topicTitle: v.topicTitle }]
            if (p.sourceType === 'MIX_SET_TOPIC' && v.topicId != null) {
              this.selectedTopic = { id: v.topicId, title: v.topicTitle || '' }
              this.selectedTopicId = v.topicId
              this.topicOptions = [{ id: v.topicId, title: v.topicTitle || '' }]
            }
          }).catch(e => this.$message.error(e.message))
        }).catch(e => this.$message.error(e.message))
      }
    },
    closePModal() {
      if (!this.pModal.saving) this.pModal.visible = false
    },
    onPTypeChange() {
      this.selectedTopic = null
      this.selectedTopicId = undefined
      this.selectedVideo = null
      this.selectedVideoId = undefined
      this.topicOptions = []
      this.videoOptions = []
    },
    /* 专题搜索 */
    debouncedTopicSearch(val) {
      clearTimeout(this.topicSearchTimer)
      this.topicSearchTimer = setTimeout(() => this.topicSearch(val), 350)
    },
    topicSearch(val) {
      if (!val) { this.topicOptions = []; return }
      this.topicLoading = true
      get('/vod/questionnaire/video/search', { type: 'MIX_SET_TOPIC', title: val, limit: 20 }).then(body => {
        this.topicOptions = body.data || []
      }).catch(e => this.$message.error(e.message)).finally(() => {
        this.topicLoading = false
      })
    },
    onTopicPicked(tid) {
      const t = this.topicOptions.filter(x => x.id === tid)[0]
      if (t) {
        this.selectedTopic = { id: t.id, title: t.title }
      }
      this.selectedVideo = null
      this.selectedVideoId = undefined
      this.videoOptions = []
    },
    /* 视频搜索 */
    debouncedVideoSearch(val) {
      clearTimeout(this.videoSearchTimer)
      this.videoSearchTimer = setTimeout(() => this.videoSearch(val), 350)
    },
    videoSearch(val) {
      if (!val) { this.videoOptions = []; return }
      if (this.pForm.sourceType === 'MIX_SET_TOPIC' && !this.selectedTopicId) {
        this.$message.warning('请先在上方选择专题')
        return
      }
      this.videoLoading = true
      const params = { type: this.pForm.sourceType, title: val, limit: 20 }
      if (this.pForm.sourceType === 'MIX_SET_TOPIC') params.topicId = this.selectedTopicId
      get('/vod/questionnaire/video/search', params).then(body => {
        this.videoOptions = body.data || []
      }).catch(e => this.$message.error(e.message)).finally(() => {
        this.videoLoading = false
      })
    },
    onVideoPicked(vid) {
      const v = this.videoOptions.filter(x => x.id === vid)[0]
      if (v) {
        this.selectedVideo = { id: v.id, title: v.title, topicId: v.topicId, topicTitle: v.topicTitle }
      }
    },
    savePoint() {
      if (!this.selectedVideo || !this.selectedVideo.id) {
        this.$message.error('请先搜索并选择视频')
        return
      }
      if (this.pForm.sourceType === 'MIX_SET_TOPIC' && !this.selectedTopic) {
        this.$message.error('请先选择专题')
        return
      }
      if (!this.pForm.questionnaireId) {
        this.$message.error('请选择问卷')
        return
      }
      if (this.pForm.triggerTime == null || this.pForm.triggerTime < 0) {
        this.$message.error('请填写合法的触发秒数')
        return
      }
      const payload = {
        sourceType: this.pForm.sourceType,
        sourceId: this.selectedVideo.id,
        questionnaireId: this.pForm.questionnaireId,
        triggerTime: this.pForm.triggerTime,
        requireAuth: this.pForm.requireAuth ? 1 : 0
      }
      this.pModal.saving = true
      const req = this.pModal.id ? put('/vod/questionnaire/point', Object.assign({ id: this.pModal.id }, payload)) : post('/vod/questionnaire/point', payload)
      req.then(() => {
        this.$message.success(this.pModal.id ? '点位已更新' : '点位已创建')
        this.pModal.visible = false
        this.loadPoints(this.pPage)
      }).catch(e => this.$message.error(e.message)).finally(() => {
        this.pModal.saving = false
      })
    },

    /* ===== 统计 ===== */
    showStat(pointId) {
      this.statModal.pointId = pointId
      this.statModal.visible = true
      this.statData = null
      this.statLoading = true
      get('/vod/questionnaire/point/' + pointId + '/statistics').then(body => {
        this.statData = body.data
      }).catch(e => {
        this.statData = null
        this.$message.error(e.message)
      }).finally(() => {
        this.statLoading = false
      })
    },
    closeStatModal() {
      this.statModal.visible = false
    }
  },
  mounted() {
    this.loadPoints(1)
  }
}
</script>

<style scoped>
.vod-question-page { padding: 16px; }
.cfg-hint { font-size: 12px; color: #8b9aab; }
.mono { font-family: monospace; color: #5c6b7a; margin-left: 4px; font-size: 12px; }
.toolbar { margin-bottom: 12px; }
.stat-loading { text-align: center; color: #8b9aab; padding: 30px 0; }
.stat-grid {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 16px;
}
.stat-card {
  border: 1px solid #eee; border-radius: 4px; padding: 10px 12px; background: #fafafa;
}
.stat-card .k { font-size: 12px; color: #8b9aab; }
.stat-card .v { font-size: 22px; font-weight: 600; margin-top: 2px; }
.stat-block {
  border: 1px solid #eee; border-radius: 4px; padding: 10px 12px; margin-bottom: 12px;
}
.stat-block-title { font-weight: 600; margin-bottom: 6px; }
.bar-row { display: flex; align-items: center; margin: 4px 0; }
.bar-lbl { width: 70px; flex: none; font-size: 12px; color: #555; }
.bar-lbl.wide { width: 130px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.bar-track { flex: 1; background: #f0f0f0; border-radius: 2px; height: 14px; margin: 0 8px; }
.bar-fill { height: 14px; background: #1890ff; border-radius: 2px; transition: width .3s; }
.bar-num { width: 40px; flex: none; text-align: right; font-size: 12px; }
@media (max-width: 720px) {
  .stat-grid { grid-template-columns: repeat(2, 1fr); }
}
</style>
